import { alternativeDatesRequest } from '@web-platform/shared-universal-data-access-frontend/src/network/clients/alternativeDates';
import { sonarV3CreateRequest } from '@web-platform/shared-universal-data-access-frontend/src/network/clients/sonar';
import { sonarV3AlphaCreateRequest } from '@web-platform/shared-universal-data-access-frontend/src/network/clients/sonarV3Alpha';
import { http } from '@web-platform/shared-universal-data-access-frontend/src/network/internals/clients/http';
import {
  ALTERNATIVE_DATES_SESSION,
  SONAR_V3_ALPHA_CREATE_SESSION,
  SONAR_V3_CREATE_SESSION,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { isAbortError } from '@web-platform/shared-universal-utils-frontend/src/helpers/abortErrorHelpers';
import { logError } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import pickBy from 'lodash/pickBy';

import { mutationResponseReceived, mutationRequestSent } from '../../actions/network';

import type {
  GenericErrorPayloadResponse,
  PayloadResponse,
  TimeoutErrorPayloadResponse,
} from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type {
  MutationItem,
  MutationState,
} from '@web-platform/shared-universal-types/src/redux/state/network/mutations';
import type { ActionType } from '@web-platform/shared-universal-types/src/redux/state/state-shared-types';
import type { Dispatch } from 'redux';

function isMutationAwaitingRequest(item: MutationItem) {
  return !item.complete && !item.inFlight;
}

function getMutationsAwaitingRequest(state: MutationState): MutationState {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return pickBy(state, (item: MutationItem) => isMutationAwaitingRequest(item)) as MutationState;
}

function getRequestMutationClient(item: MutationItem): () => Promise<PayloadResponse> {
  if (item.type === SONAR_V3_CREATE_SESSION) {
    return () => sonarV3CreateRequest(item.request);
  }

  if (item.type === SONAR_V3_ALPHA_CREATE_SESSION) {
    // only for banana traffic
    return () => sonarV3AlphaCreateRequest(item.request);
  }

  if (item.type === ALTERNATIVE_DATES_SESSION) {
    // only for acorn traffic
    return () => alternativeDatesRequest(item.request);
  }

  return () => http(item.request);
}

function getErrorPayload(item: MutationItem, err: any): PayloadResponse {
  const isAbortTimeout = isAbortError(err);
  const isSonarCreateSession =
    item.type === SONAR_V3_CREATE_SESSION || item.type === SONAR_V3_ALPHA_CREATE_SESSION;

  let errorPayload: PayloadResponse;
  if (isAbortTimeout && isSonarCreateSession) {
    const timeoutError: TimeoutErrorPayloadResponse = {
      headers: null,
      error: true,
      body: null,
      status: 504,
    };
    errorPayload = timeoutError;
  } else {
    const genericError: GenericErrorPayloadResponse = {
      headers: null,
      error: true,
      body: null,
      status: null,
    };
    errorPayload = genericError;
  }
  return errorPayload;
}

export function handleImmediateRequests(state: MutationState, dispatch: Dispatch<ActionType>) {
  return Promise.all(
    Object.keys(state).map((itemId) => {
      const item = state[itemId];

      dispatch(mutationRequestSent(item.id, item.type));
      const request = getRequestMutationClient(item);
      return request()
        .then(
          (response: PayloadResponse) => {
            dispatch(mutationResponseReceived(item.id, item.type, item.variables, response));
          },
          (err) => {
            const errorPayload = getErrorPayload(item, err);
            dispatch(mutationResponseReceived(item.id, item.type, item.variables, errorPayload));
          },
        )
        .catch((err) => {
          logError({
            component: 'actUponMutationState',
            error: err,
            message: 'Error with actUponMutationState',
            stack: err && err.stack,
          });
        });
    }),
  );
}

export function actUponMutationState(
  getState: () => MutationState,
  dispatch: Dispatch<ActionType>,
) {
  return handleImmediateRequests(getMutationsAwaitingRequest(getState()), dispatch);
}
